import { studyDemoReturnTo } from './lifecycle';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { getLiveStudyExperience } from './mode';
import { isStudyMaterialReader } from '@/app/learnNavigation';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { LearnPageFrame } from '@/app/LearnPageFrame';
import { LearnDock } from '@/app/LearnDock';
import { StudyToolEvaluationContext } from '@/pages/learn/study/StudyToolEvaluationContext';
import { SampleWorkspaceFrame } from './SampleWorkspaceFrame';

export function StudyStudentExperienceLayout() {
  const { pathname } = useLocation();
  const live = getLiveStudyExperience();
  const balance = useQuery({
    queryKey: ['study', 'teacher-experience', 'payer'],
    queryFn: () => api<{ stars_balance: number }>('/study/teacher-experience/payer'),
    enabled: !!live,
    refetchInterval: live ? 10_000 : false,
  });
  const teachingUrl = `${(import.meta.env.VITE_INSTITUTION_PORTAL_URL ?? 'https://institution.airbotix.ai').replace(/\/$/, '')}/study/explore`;
  const exitTo = studyDemoReturnTo(window.location.search, window.location.origin);
  const expired = balance.error instanceof ApiError && balance.error.status === 401;
  const product = (
    <LearnPageFrame
      immersive
      fluid
      dock={isStudyMaterialReader(pathname) ? undefined : <LearnDock />}
      banner={
        live ? (
          <aside
            aria-label="Student experience mode"
            className="z-40 shrink-0 border-b border-hairline bg-wash-sunshine px-4 py-2 text-xs text-ink"
          >
            <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-2">
              <p role={expired ? 'alert' : undefined}>
                <strong>Student experience</strong>{' '}
                {expired
                  ? 'Your experience session has ended. Reopen it to continue with your saved work.'
                  : `Your private learner · AI uses ${live.payer.name} Stars (${balance.data?.stars_balance ?? live.payer.stars_balance} available). Your work is saved.`}
              </p>
              <div className="flex gap-3">
                {expired && (
                  <a
                    className="font-bold underline"
                    href={teachingUrl.replace(/\/study\/explore$/, '/study/experience')}
                  >
                    Reopen student experience
                  </a>
                )}
                {live && (
                  <a className="font-bold underline" href={teachingUrl}>
                    Back to teaching
                  </a>
                )}
              </div>
            </div>
          </aside>
        ) : undefined
      }
    >
      <Outlet />
    </LearnPageFrame>
  );
  return (
    <StudyToolEvaluationContext.Provider value={!live}>
      <div className="h-dvh overflow-hidden bg-canvas">
        {live ? product : <SampleWorkspaceFrame exitTo={exitTo}>{product}</SampleWorkspaceFrame>}
      </div>
    </StudyToolEvaluationContext.Provider>
  );
}
export function StudyExperienceBoundaryPage() {
  const { pathname } = useLocation();
  return (
    <main className="mx-auto max-w-2xl p-8">
      <h1 className="text-2xl font-extrabold text-ink">Explore the student Study experience</h1>
      <p className="my-4 text-slate2">
        {pathname.includes('class')
          ? 'Classes are not connected to this fictional student workspace. No real classroom or child account was opened.'
          : pathname.includes('create')
            ? 'Create studios are outside this Study example. This action did not create a real project or start AI.'
            : 'This example covers Study. Other Airbotix products and account management are outside its isolated workspace.'}
      </p>
      <Link className="btn-pill-primary" to="/learn/study">
        Back to Study
      </Link>
    </main>
  );
}
